import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import "./Careers.css";
import { 
  ArrowRight,
  Briefcase,
  MapPin,
  Clock,
  Users,
  Database,
  BarChart3,
  Building2,
  CheckCircle2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";


// Job Listings Data
const jobListings = [
  {
    id: 1,
    title: "Data Analyst",
    department: "Data Science",
    location: "Remote / Guaynabo, PR",
    type: "Full-time",
    experience: "5+ years",
    icon: BarChart3,
    description: "Join our data science team to analyze consumer behavior patterns, build predictive models, and deliver actionable insights that drive campaign performance for Fortune 500 clients.",
    responsibilities: [
      "Analyze large-scale consumer datasets (245M+ profiles) to identify trends and opportunities",
      "Build and maintain dashboards for real-time campaign performance monitoring",
      "Develop predictive models for audience segmentation and targeting optimization",
      "Collaborate with engineering to implement data pipelines and ETL processes",
      "Present findings and recommendations to clients and stakeholders"
    ],
    requirements: [
      "5+ years of experience in data analysis or business intelligence",
      "Proficiency in SQL, Python, and data visualization tools (Tableau, Looker)",
      "Experience with statistical modeling and machine learning techniques",
      "Strong communication skills with ability to translate data into business insights",
      "Bachelor's degree in Statistics, Mathematics, Computer Science, or related field"
    ]
  },
  {
    id: 2,
    title: "Database Administrator",
    department: "Engineering",
    location: "Remote / Guaynabo, PR",
    type: "Full-time",
    experience: "5+ years",
    icon: Database,
    description: "Manage and optimize our high-performance database infrastructure supporting real-time consumer intelligence and campaign execution at scale across MongoDB and PostgreSQL environments.",
    responsibilities: [
      "Design, implement, and maintain MongoDB and PostgreSQL database systems",
      "Optimize query performance for real-time data access (millions of records/second)",
      "Implement backup, recovery, and disaster recovery procedures",
      "Monitor database health, capacity planning, and performance tuning",
      "Collaborate with development teams on schema design and data modeling"
    ],
    requirements: [
      "5+ years of experience as a Database Administrator",
      "Expert-level knowledge of MongoDB (sharding, replication, aggregation pipelines)",
      "Strong experience with PostgreSQL (partitioning, indexing, query optimization)",
      "Experience with database security, encryption, and compliance (SOC 2, GDPR)",
      "Familiarity with cloud platforms (AWS, GCP) and containerization (Docker, Kubernetes)"
    ]
  },
  {
    id: 3,
    title: "VP Nationwide Accounts",
    department: "Sales & Partnerships",
    location: "Remote (Travel Required)",
    type: "Full-time",
    experience: "5+ years",
    icon: Building2,
    description: "Lead strategic relationships with enterprise clients across the nation. Drive revenue growth by positioning IntentLogic's data-driven marketing solutions to Fortune 1000 brands.",
    responsibilities: [
      "Develop and execute strategic account plans for nationwide enterprise clients",
      "Build C-level relationships with marketing, analytics, and technology leaders",
      "Drive multi-million dollar revenue growth through solution selling",
      "Collaborate with product and delivery teams to ensure client success",
      "Represent IntentLogic at industry conferences and executive briefings"
    ],
    requirements: [
      "5+ years of enterprise sales experience in MarTech, AdTech, or data services",
      "Proven track record of closing $1M+ deals with Fortune 500 companies",
      "Deep understanding of performance marketing, CDP, and data-driven advertising",
      "Strong executive presence and consultative selling skills",
      "Willingness to travel 40-50% for client meetings and industry events"
    ]
  }
];

// Company Benefits
const benefits = [
  { title: "Remote-First Culture", desc: "Work from anywhere with flexible hours" },
  { title: "Competitive Compensation", desc: "Top-tier salary plus performance bonuses" },
  { title: "Health & Wellness", desc: "Comprehensive medical, dental, and vision" },
  { title: "Professional Growth", desc: "Conference attendance and learning stipend" },
  { title: "Equity Participation", desc: "Stock options for all team members" },
  { title: "Unlimited PTO", desc: "Take the time you need to recharge" }
];

// Hero Section
const CareersHero = () => {
  return (
    <section className="careers-hero" data-testid="careers-hero">
      <div className="careers-hero-content">
        <span className="careers-label">CAREERS</span>
        <h1>Build the Future of Performance Marketing</h1>
        <p className="careers-hero-subtitle">
          Join a team of data scientists, engineers, and marketing experts 
          transforming how brands connect with consumers. We're growing fast 
          and looking for exceptional talent.
        </p>
        <div className="careers-hero-stats">
          <div className="hero-stat">
            <span className="stat-value">245M+</span>
            <span className="stat-label">Consumer Profiles</span>
          </div>
          <div className="hero-stat">
            <span className="stat-value">500+</span>
            <span className="stat-label">Campaigns Executed</span>
          </div>
          <div className="hero-stat">
            <span className="stat-value">30+</span>
            <span className="stat-label">Team Members</span>
          </div>
        </div>
      </div>
    </section>
  );
};

// Job Card Component
const JobCard = ({ job, onApply }) => {
  const [expanded, setExpanded] = useState(false);
  const IconComponent = job.icon;

  return (
    <div className={`job-card ${expanded ? 'expanded' : ''}`} data-testid={`job-card-${job.id}`}>
      <div className="job-card-header" onClick={() => setExpanded(!expanded)}>
        <div className="job-icon">
          <IconComponent size={24} />
        </div>
        <div className="job-info">
          <h3>{job.title}</h3>
          <div className="job-meta">
            <span><Briefcase size={14} /> {job.department}</span>
            <span><MapPin size={14} /> {job.location}</span>
            <span><Clock size={14} /> {job.type}</span>
            <span><Users size={14} /> {job.experience}</span>
          </div>
        </div>
        <div className="job-expand-icon">
          <ArrowRight size={20} className={expanded ? 'rotated' : ''} />
        </div>
      </div>
      
      {expanded && (
        <div className="job-card-details">
          <p className="job-description">{job.description}</p>
          
          <div className="job-section">
            <h4>Responsibilities</h4>
            <ul>
              {job.responsibilities.map((item, i) => (
                <li key={i}>
                  <CheckCircle2 size={16} />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
          
          <div className="job-section">
            <h4>Requirements</h4>
            <ul>
              {job.requirements.map((item, i) => (
                <li key={i}>
                  <CheckCircle2 size={16} />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
          
          <Button 
            className="btn-primary-zeta apply-btn"
            onClick={(e) => {
              e.stopPropagation();
              onApply(job.title);
            }}
          >
            Apply for this Position
            <ArrowRight size={18} />
          </Button>
        </div>
      )}
    </div>
  );
};

// Open Positions Section
const OpenPositions = ({ onApply }) => {
  return (
    <section className="careers-positions" data-testid="careers-positions">
      <div className="careers-section-header">
        <span className="careers-section-label">OPEN POSITIONS</span>
        <h2>Join Our Team</h2>
        <p>We're looking for talented individuals who are passionate about data, technology, and delivering results.</p>
      </div>
      
      <div className="jobs-list">
        {jobListings.map((job) => (
          <JobCard key={job.id} job={job} onApply={onApply} />
        ))}
      </div>
    </section>
  );
};

// Benefits Section
const BenefitsSection = () => {
  return (
    <section className="careers-benefits" data-testid="careers-benefits">
      <div className="careers-section-header">
        <span className="careers-section-label">WHY INTENTLOGIC</span>
        <h2>Benefits & Perks</h2>
        <p>We invest in our team's success, well-being, and growth.</p>
      </div>
      
      <div className="benefits-grid">
        {benefits.map((benefit, i) => (
          <div key={i} className="benefit-card">
            <h4>{benefit.title}</h4>
            <p>{benefit.desc}</p>
          </div>
        ))}
      </div>
    </section>
  );
};

// Culture Section
const CultureSection = () => {
  return (
    <section className="careers-culture" data-testid="careers-culture">
      <div className="careers-culture-content">
        <div className="culture-text">
          <span className="careers-section-label">OUR CULTURE</span>
          <h2>Data-Driven. Results-Focused. Human-First.</h2>
          <p>
            At IntentLogic, we combine the analytical rigor of a data science lab 
            with the energy of a high-growth startup. We believe in:
          </p>
          <ul className="culture-values">
            <li>
              <strong>Intellectual Curiosity</strong> — We question assumptions 
              and let data guide our decisions.
            </li>
            <li>
              <strong>Ownership Mentality</strong> — Every team member has the 
              autonomy to drive meaningful impact.
            </li>
            <li>
              <strong>Transparent Communication</strong> — We share context freely 
              and debate ideas openly.
            </li>
            <li>
              <strong>Client Obsession</strong> — We measure success by the 
              outcomes we deliver for our partners.
            </li>
          </ul>
        </div>
        <div className="culture-visual">
          <div className="culture-stat-card">
            <div className="culture-stat">
              <span className="stat-number">94%</span>
              <span className="stat-text">Employee Satisfaction</span>
            </div>
            <div className="culture-stat">
              <span className="stat-number">4.8</span>
              <span className="stat-text">Glassdoor Rating</span>
            </div>
            <div className="culture-stat">
              <span className="stat-number">2x</span>
              <span className="stat-text">Team Growth (YoY)</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// CTA Section
const CareersCTA = () => (
  <section className="careers-cta-section" data-testid="careers-cta">
    <div className="careers-cta-content">
      <h2>Don't See the Right Role?</h2>
      <p>
        We're always looking for exceptional talent. Send us your resume 
        and tell us how you'd contribute to our mission.
      </p>
      <Button 
        className="btn-primary-zeta"
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      >
        Submit General Application
        <ArrowRight size={18} />
      </Button>
    </div>
  </section>
);

// Main Careers Page Component
function CareersPage() {
  const [showModal, setShowModal] = useState(false);
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  const handleApply = () => {
    setShowModal(true);
  };

  return (
    <Layout>
      <div className="careers-page" data-testid="careers-page">
        <SEO 
          title="Careers - Join Our Team"
          description="Join IntentLogic and help shape the future of performance marketing. Explore open positions in data science, engineering, marketing, and more."
          keywords="IntentLogic careers, marketing jobs, data science jobs, tech jobs, job openings, career opportunities, work at IntentLogic, marketing careers"
          canonical="/careers"
        />
        <CareersHero />
        <OpenPositions onApply={handleApply} />
        <BenefitsSection />
        <CultureSection />
        <CareersCTA />
      </div>
    </Layout>
  );
}

export default CareersPage;
